import { Users, UserPlus, Ban, CheckCircle, AlertTriangle, FileText, ChevronUp, ChevronDown, RefreshCw } from 'lucide-react';
import { ApplicationUser, PdpaConsent, PdpaRequest } from '../../../api/admin';
import { cn } from '../../../utils/responsive';
import { formatThaiDate, formatThaiDateTime } from '../../../utils/dateUtils';

interface AppUserSectionProps {
    users: ApplicationUser[];
    total: number;
    isLoading: boolean;
    isMobileOrTablet: boolean;
    onAddUser: () => void;
    onToggleStatus: (user: ApplicationUser) => void;
    onTogglePdpa: (user: ApplicationUser) => void;
    pdpaExpandedUser: string | null;
    pdpaLoadingUser: string | null;
    pdpaData: Record<string, { consents: PdpaConsent[]; requests: PdpaRequest[] }>;
}

export function AppUserSection({
    users,
    total,
    isLoading,
    isMobileOrTablet,
    onAddUser,
    onToggleStatus,
    onTogglePdpa,
    pdpaExpandedUser,
    pdpaLoadingUser,
    pdpaData,
}: AppUserSectionProps) {
    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-stone-200 dark:border-gray-700 overflow-hidden">
            <div className={cn(
                "flex items-center border-b border-stone-200 dark:border-gray-700 px-4 lg:px-6 py-3 lg:py-4",
                isMobileOrTablet ? "flex-col gap-3" : "justify-between"
            )}>
                <h2 className={cn(
                    "font-semibold text-stone-900 dark:text-gray-100 flex items-center gap-2",
                    isMobileOrTablet ? "text-base w-full" : "text-lg"
                )}>
                    <Users className={cn(
                        "text-yellow-600",
                        isMobileOrTablet ? "w-4 h-4" : "w-5 h-5"
                    )} />
                    ลูกค้า ({users.length}/{total})
                </h2>
                <button
                    onClick={onAddUser}
                    className={cn(
                        "inline-flex items-center justify-center gap-2",
                        "rounded-lg bg-gradient-to-r from-yellow-400 to-orange-500",
                        "px-4 py-2 text-sm font-semibold text-white",
                        "shadow-sm hover:shadow-accent hover:scale-[1.02]",
                        "transition-all duration-200 touch-target",
                        isMobileOrTablet && "w-full"
                    )}
                >
                    <UserPlus className="w-4 h-4" />
                    เพิ่มผู้ใช้
                </button>
            </div>
            {isLoading ? (
                <div className="flex items-center justify-center py-12 text-stone-500 dark:text-gray-400">
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                    กำลังโหลดข้อมูล...
                </div>
            ) : users.length === 0 ? (
                <div className="py-10 text-center text-sm text-stone-500 dark:text-gray-400">ไม่พบผู้ใช้ที่ตรงกับเงื่อนไข</div>
            ) : (
                <ul className="divide-y divide-stone-200 dark:divide-gray-700">
                    {users.map(user => (
                        <li key={user.id} className={cn(
                            "space-y-3",
                            isMobileOrTablet ? "p-4" : "p-6"
                        )}>
                            <div className={cn(
                                "flex items-start gap-3",
                                isMobileOrTablet ? "flex-col" : "justify-between"
                            )}>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-stone-500 dark:text-gray-400 font-mono">ID: {user.id}</p>
                                    <p className={cn(
                                        "font-semibold text-stone-900 dark:text-gray-100 mt-1",
                                        isMobileOrTablet ? "text-base" : "text-lg"
                                    )}>
                                        {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'ไม่ระบุชื่อ'}
                                    </p>
                                    <p className="text-sm text-stone-600 dark:text-gray-400 font-mono">{user.phoneNumber}</p>
                                </div>
                                <div className={cn(
                                    "flex items-center gap-2",
                                    isMobileOrTablet && "w-full"
                                )}>
                                    {user.status === 'suspended' ? (
                                        <button
                                            onClick={() => onToggleStatus(user)}
                                            className={cn(
                                                "inline-flex items-center justify-center gap-2",
                                                "rounded-full px-3 py-1.5 text-xs font-medium",
                                                "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/40",
                                                "transition-all duration-200 touch-target",
                                                isMobileOrTablet && "w-full"
                                            )}
                                        >
                                            <Ban className="w-3 h-3" />
                                            ถูกระงับ
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => onToggleStatus(user)}
                                            className={cn(
                                                "inline-flex items-center justify-center gap-2",
                                                "rounded-full px-3 py-1.5 text-xs font-medium",
                                                "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/40",
                                                "transition-all duration-200 touch-target",
                                                isMobileOrTablet && "w-full"
                                            )}
                                        >
                                            <CheckCircle className="w-3 h-3" />
                                            ใช้งานอยู่
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className={cn(
                                "grid gap-3 lg:gap-4 text-sm text-stone-600 dark:text-gray-400",
                                isMobileOrTablet ? "grid-cols-2" : "grid-cols-4"
                            )}>
                                <div className="space-y-0.5">
                                    <p className="text-xs font-medium text-stone-500 dark:text-gray-400">แต้มสะสม</p>
                                    <p className="font-mono font-semibold text-stone-900 dark:text-gray-100">
                                        {Number(user.loyaltyPoints).toLocaleString('th-TH')}
                                    </p>
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-xs font-medium text-stone-500 dark:text-gray-400">Wallet</p>
                                    <p className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                                        ฿{Number(user.walletBalance).toLocaleString('th-TH')}
                                    </p>
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-xs font-medium text-stone-500 dark:text-gray-400">ระดับ</p>
                                    <p className="font-semibold text-stone-900 dark:text-gray-100">Level {user.loyaltyLevel}</p>
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-xs font-medium text-stone-500 dark:text-gray-400">สมัครเมื่อ</p>
                                    <p className="text-xs text-stone-700 dark:text-gray-300">{formatThaiDate(user.createdAt)}</p>
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-xs font-medium text-stone-500 dark:text-gray-400">PDPA</p>
                                    <p className="text-xs">{user.consentPdpa ? '✅ ยินยอมแล้ว' : '⚠️ ยังไม่ยินยอม'}</p>
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-xs font-medium text-stone-500 dark:text-gray-400">Marketing</p>
                                    <p className="text-xs">{user.consentMarketing ? '✅ ยอมรับ' : '❌ ไม่ยอมรับ'}</p>
                                </div>
                            </div>
                            {/* Suspension info - show when user is suspended */}
                            {user.status === 'suspended' && user.suspendReason && (
                                <div className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 p-4 text-sm">
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
                                        <div className="space-y-1">
                                            <p className="font-semibold text-red-700 dark:text-red-400">ผู้ใช้ถูกระงับการใช้งาน</p>
                                            <p className="text-red-600 dark:text-red-400">
                                                <span className="font-medium">เหตุผล:</span> {user.suspendReason}
                                            </p>
                                            {user.suspendReasonDetail && (
                                                <p className="text-red-600 dark:text-red-400">
                                                    <span className="font-medium">รายละเอียด:</span> {user.suspendReasonDetail}
                                                </p>
                                            )}
                                            {user.suspendedAt && (
                                                <p className="text-red-500 dark:text-red-400 text-xs">
                                                    ระงับเมื่อ: {formatThaiDateTime(user.suspendedAt)}
                                                </p>
                                            )}
                                            <button
                                                onClick={() => onToggleStatus(user)}
                                                className="mt-2 inline-flex items-center gap-1 rounded-lg border border-red-300 dark:border-red-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30"
                                            >
                                                <RefreshCw className="h-3 w-3" />
                                                ยกเลิกการระงับ
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className={cn(
                                "flex items-center pt-3 text-xs text-stone-500 dark:text-gray-400 border-t border-stone-100 dark:border-gray-700",
                                isMobileOrTablet ? "flex-col gap-2" : "justify-between"
                            )}>
                                <span className={cn(isMobileOrTablet && "w-full text-center")}>
                                    เข้าใช้ล่าสุด: {user.lastLogin ? formatThaiDate(user.lastLogin) : 'ไม่เคย'}
                                </span>
                                <button
                                    onClick={() => onTogglePdpa(user)}
                                    className={cn(
                                        "inline-flex items-center justify-center gap-1.5",
                                        "rounded-lg border border-stone-300 dark:border-gray-600 px-3 py-1.5",
                                        "text-xs font-medium text-stone-700 dark:text-gray-100",
                                        "hover:bg-stone-50 dark:hover:bg-gray-700 hover:border-yellow-400",
                                        "transition-all duration-200 touch-target",
                                        isMobileOrTablet && "w-full"
                                    )}
                                >
                                    <FileText className="h-3 w-3" />
                                    หลักฐาน PDPA
                                    {pdpaExpandedUser === user.id ? (
                                        <ChevronUp className="h-3 w-3" />
                                    ) : (
                                        <ChevronDown className="h-3 w-3" />
                                    )}
                                </button>
                            </div>
                            {pdpaExpandedUser === user.id && (
                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 text-sm text-gray-600 dark:text-gray-400">
                                    {pdpaLoadingUser === user.id && !pdpaData[user.id] ? (
                                        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                            <RefreshCw className="h-4 w-4 animate-spin" />
                                            กำลังดึงข้อมูล PDPA...
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <div>
                                                <h4 className="font-semibold text-gray-700 dark:text-gray-300">หลักฐานการยินยอม</h4>
                                                {pdpaData[user.id]?.consents && pdpaData[user.id]?.consents.length > 0 ? (
                                                    <ul className="mt-2 space-y-2">
                                                        {pdpaData[user.id]?.consents.map(consent => (
                                                            <li key={consent.id} className="rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                                                                <p className="text-sm font-medium text-gray-800 dark:text-gray-100">รุ่น {consent.consentVersion} • {consent.consentType}</p>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">บันทึกเมื่อ {formatThaiDateTime(consent.collectedAt)}</p>
                                                                {consent.evidenceUrl && (
                                                                    <a
                                                                        href={consent.evidenceUrl}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="mt-1 inline-flex items-center text-xs text-blue-600 hover:underline"
                                                                    >
                                                                        ดูหลักฐาน
                                                                    </a>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">ยังไม่มีการบันทึกการยินยอม</p>
                                                )}
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-700 dark:text-gray-300">คำขอของเจ้าของข้อมูล</h4>
                                                {pdpaData[user.id]?.requests && pdpaData[user.id]?.requests.length > 0 ? (
                                                    <ul className="mt-2 space-y-2">
                                                        {pdpaData[user.id]?.requests.map(request => (
                                                            <li key={request.id} className="rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                                                                <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{request.requestType}</p>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">สถานะ: {request.status}</p>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">ยื่นเมื่อ {formatThaiDateTime(request.submittedAt)}</p>
                                                                {request.notes && <p className="text-xs text-gray-500 dark:text-gray-400">หมายเหตุ: {request.notes}</p>}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">ไม่มีคำขอจากผู้ใช้นี้</p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
